import {
  createLogEntry,
  getErrorDefinition,
  toJsonLine,
  type ClientErrorReport,
  type ErrorCode,
  type LogEntry,
  type LogSeverity,
} from '@ebu/contracts';
import { requestToPromise, txDone } from '../adapters/idb';

/**
 * Browser diagnostics: sanitised structured entries buffered in IndexedDB (bounded ring) and
 * exportable as JSONL. Falls back to memory-only with WEB-LOG-001 if IndexedDB is unavailable.
 * No child data: entries go through @ebu/contracts sanitisation; remote reports carry only the
 * registry title for the code, never raw exception text.
 */

export interface LogFields {
  code?: ErrorCode;
  origin?: string;
  context?: Record<string, unknown>;
}

export type ErrorReporter = (report: ClientErrorReport) => Promise<void>;

export interface BrowserLoggerOptions {
  idbFactory?: IDBFactory | undefined;
  dbName?: string;
  maxEntries?: number;
  sessionId?: string;
  appVersion: string;
  clock?: () => Date;
  currentRoute?: () => string;
  reporter?: ErrorReporter | undefined;
}

const STORE = 'entries';
const DB_VERSION = 1;

function randomId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

export class BrowserLogger {
  readonly sessionId: string;
  private readonly opts: Required<
    Omit<BrowserLoggerOptions, 'idbFactory' | 'reporter' | 'sessionId'>
  > &
    Pick<BrowserLoggerOptions, 'idbFactory' | 'reporter'>;
  private db: IDBDatabase | undefined;
  private memory: LogEntry[] = [];
  private pending: Promise<void> = Promise.resolve();
  private initPromise: Promise<void> | undefined;
  persistent = false;

  constructor(options: BrowserLoggerOptions) {
    this.sessionId = options.sessionId ?? randomId();
    this.opts = {
      dbName: 'ebu-kids-world-diagnostics',
      maxEntries: 1000,
      clock: () => new Date(),
      currentRoute: () => (typeof location === 'undefined' ? '/' : location.pathname),
      ...options,
    };
  }

  init(): Promise<void> {
    this.initPromise ??= this.openDb();
    return this.initPromise;
  }

  private async openDb(): Promise<void> {
    const factory = this.opts.idbFactory;
    try {
      if (!factory) throw new Error('IndexedDB is not available in this browser');
      const req = factory.open(this.opts.dbName, DB_VERSION);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE)) {
          req.result.createObjectStore(STORE, { autoIncrement: true });
        }
      };
      this.db = await requestToPromise(req);
      this.persistent = true;
      // Persist anything logged before init finished.
      const early = [...this.memory];
      for (const entry of early) this.enqueuePersist(entry);
    } catch (error) {
      this.persistent = false;
      this.record(
        'WARN',
        'diagnostics',
        'Diagnostics buffer is memory-only for this page session',
        {
          code: 'WEB-LOG-001',
          origin: 'apps/web/src/diagnostics/browser-logger.ts:BrowserLogger.openDb',
          context: { err: error },
        },
      );
    }
  }

  info(module: string, message: string, fields: LogFields = {}): LogEntry {
    return this.log('INFO', module, message, fields);
  }
  warn(module: string, message: string, fields: LogFields = {}): LogEntry {
    return this.log('WARN', module, message, fields);
  }
  error(module: string, message: string, fields: LogFields = {}): LogEntry {
    return this.log('ERROR', module, message, fields);
  }
  critical(module: string, message: string, fields: LogFields = {}): LogEntry {
    return this.log('CRITICAL', module, message, fields);
  }

  log(severity: LogSeverity, module: string, message: string, fields: LogFields = {}): LogEntry {
    const entry = this.record(severity, module, message, fields);
    if ((severity === 'ERROR' || severity === 'CRITICAL') && fields.code && this.opts.reporter) {
      this.report(severity, module, fields.code);
    }
    return entry;
  }

  private record(
    severity: LogSeverity,
    module: string,
    message: string,
    fields: LogFields,
  ): LogEntry {
    const entry = createLogEntry({
      severity,
      module,
      message,
      correlationId: this.sessionId,
      code: fields.code,
      origin: fields.origin,
      context: fields.context,
      now: this.opts.clock(),
    });
    this.memory.push(entry);
    if (this.memory.length > this.opts.maxEntries)
      this.memory.splice(0, this.memory.length - this.opts.maxEntries);
    if (this.persistent) this.enqueuePersist(entry);
    return entry;
  }

  private report(severity: 'ERROR' | 'CRITICAL', module: string, code: ErrorCode): void {
    const report: ClientErrorReport = {
      code,
      severity,
      module:
        module
          .toLowerCase()
          .replace(/[^a-z0-9-]/g, '-')
          .slice(0, 64) || 'web',
      message: getErrorDefinition(code).title,
      correlationId: this.sessionId,
      appVersion: this.opts.appVersion,
      route:
        this.opts
          .currentRoute()
          .replace(/[^A-Za-z0-9/_-]/g, '')
          .slice(0, 120) || '/',
      occurredAt: this.opts.clock().toISOString(),
    };
    this.opts.reporter!(report).catch((error: unknown) => {
      // Recorded locally only (no re-report) to avoid a reporting loop.
      this.record('WARN', 'diagnostics', 'Could not send error report to the service', {
        code: 'WEB-API-001',
        origin: 'apps/web/src/diagnostics/browser-logger.ts:BrowserLogger.report',
        context: { reportedCode: code, err: error },
      });
    });
  }

  private enqueuePersist(entry: LogEntry): void {
    this.pending = this.pending.then(() => this.persist(entry));
  }

  private async persist(entry: LogEntry): Promise<void> {
    const db = this.db;
    if (!db) return;
    try {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      store.add(entry);
      const count = await requestToPromise(store.count());
      const excess = count - this.opts.maxEntries;
      if (excess > 0) {
        const keys = await requestToPromise(store.getAllKeys(null, excess));
        for (const key of keys) store.delete(key);
      }
      await txDone(tx);
    } catch (error) {
      this.persistent = false;
      this.record('WARN', 'diagnostics', 'Diagnostics write failed; buffer is memory-only now', {
        code: 'WEB-LOG-001',
        origin: 'apps/web/src/diagnostics/browser-logger.ts:BrowserLogger.persist',
        context: { err: error },
      });
    }
  }

  /** Waits until all queued IndexedDB writes have settled. */
  async flush(): Promise<void> {
    await this.init();
    await this.pending;
  }

  /** Persisted entries (across page sessions) when available, else this session's memory buffer. */
  async getEntries(): Promise<LogEntry[]> {
    await this.flush();
    if (!this.db || !this.persistent) return [...this.memory];
    const tx = this.db.transaction(STORE, 'readonly');
    return requestToPromise(tx.objectStore(STORE).getAll() as IDBRequest<LogEntry[]>);
  }

  async exportJsonl(): Promise<string> {
    return (await this.getEntries()).map(toJsonLine).join('');
  }

  async clear(): Promise<void> {
    await this.flush();
    this.memory = [];
    if (this.db && this.persistent) {
      const tx = this.db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      await txDone(tx);
    }
  }

  close(): void {
    this.db?.close();
    this.db = undefined;
    this.persistent = false;
  }
}
